import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl } from 'react-intl';
// Cloud React components
import { Button, Input, Form } from 'Components';
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { NotificationManager } from 'Components/Notifications';
import { Switch, Select } from '@gpstrackit/gtc-react-controls';

//actions
import {
    clearRedux,
    openAccountApiKeyCreateEditModal,
    createEditAccountApiKey,
    getAccountApiKey,
    searchSubUsers
} from 'Redux/actions';

const CreateEditAccountApiKey = (props) => {
    const dispatch = useDispatch();
    const { intl, callback } = props;
    const { messages } = intl;

    const editCreateAccountApiKey = useRef();
    const {
        openCreateEdit: open,
        apiKey,
        id,
        loadingApiKey,
        loadingCreateEdit,
        createEditResponse: response
    } = useSelector(state => state.apiKeysRedux);

    let subUsers = useSelector(state => state.accountApikeysRedux.subUsers);
    subUsers = structuredClone(subUsers).sort(function (a, b) { return a.fullname.localeCompare(b.fullname); });
    const loadingSubUsers = useSelector(state => state.accountApikeysRedux.loading);

    const [formData, setFormData] = useState({});

    useEffect(() => {

        dispatch(searchSubUsers());

        return () => {
            dispatch(clearRedux("ACCOUNT_API_KEYS", {
                openCreateEdit: false,
                id: null,
                apiKey: null,
                loadingApiKey: false,
                errorApiKey: false,
                loadingCreateEdit: false,
                createEditResponse: null,
            }));
        }
    }, []);

    useEffect(() => {
        if (id && open) {
            dispatch(getAccountApiKey(id));
        }
    }, [id, open]);

    useEffect(() => {
        if (apiKey && open) {
            if (apiKey.error) {
                NotificationManager.error(
                    messages['errorMessage'],
                    messages['error'],
                    3000,
                    null,
                    null,
                    'warning',
                );
                handleClose();
            } else {
                setFormData({...apiKey, assignedToUser: apiKey?.userId ? true : false });
            }
        }
    }, [apiKey, open]);

    useEffect(() => {
        if (response && open) {
            if (response.error) {
                NotificationManager.error(
                    messages['errorMessage'],
                    messages['error'],
                    3000,
                    null,
                    null,
                    'warning',
                );
            } else {
                NotificationManager.success(
                    messages['accountApiKeys_success'],
                    messages['success'],
                    3000,
                    null,
                    null,
                    'success',
                );
                callback(formData.name);
                handleClose();
            }
        }
    }, [response, open]);

    const handleClose = useCallback(() => {
        dispatch(openAccountApiKeyCreateEditModal(false));
        dispatch(clearRedux("ACCOUNT_API_KEYS", {
            openCreateEdit: false,
            id: null,
            apiKey: null,
            loadingApiKey: false,
            errorApiKey: false,
            loadingCreateEdit: false,
            createEditResponse: null,
        }));
        setFormData({});
    });

    const onSubmit = useCallback(() => {
        dispatch(createEditAccountApiKey(formData));
    });

    const title = apiKey ? `${messages['accountApiKeys_editTitle']} '${apiKey?.name}'` : `${messages['accountApiKeys_createTitle']}`;

    return (
        <Modal
            title={title}
            open={open}
            loading={loadingApiKey}
            position="right"
            size="small"
            handleClose={handleClose}
        >
            <ModalBody>
                <div style={{ padding: 24, height: 650 }}>
                    <Form ref={editCreateAccountApiKey} onValidSubmit={onSubmit}>
                        <div>
                            <Input
                                id="name"
                                value={formData.name || ""}
                                onChange={(value) => setFormData(formData => ({ ...formData, name: value })) }
                                minLength={1}
                                maxLength={50}
                                label={messages['accountApiKeys_nameLabel']}
                                placeholder={messages['accountApiKeys_nameLabel']}
                                type="text"
                                required
                                requiredMessage={messages['required_field']}
                                minLengthMessage={`${messages['accountApiKeys_minLengthMessage']} 1`}
                                maxLengthMessage={`${messages['accountApiKeys_maxLengthMessage']} 30`}
                            />
                        </div>
                        <div style={{ marginTop: 20 }}>
                            <Switch
                                checked={formData?.assignedToUser == 1 ? true : false} 
                                optionLabels={[messages['on'], messages['off']]}
                                size="high"
                                labelPosition="right"
                                label={messages['accountApiKeys_user_assignedToUser']}
                                onChange={(value) => {
                                    setFormData(formData => ({ ...formData, assignedToUser: value ? true : false }));
                            }}/>
                        </div>
                        {
                            formData?.assignedToUser
                            && 
                            <div style={{ marginTop: 20 }}>
                                <Select 
                                    style={{ width: '100%' }}
                                    name="selectSubUsers"
                                    textField="fullname"
                                    label={messages['accountApiKeys_user_select']}
                                    valueField="id"
                                    placeholder={messages['accountApiKeys_user_select_place']}
                                    isMulti={false}
                                    options={subUsers || []}
                                    isDisabled={false}
                                    isLoading={loadingSubUsers}
                                    showPillsOutside={true}
                                    isClearable={true}
                                    isSearchable={true}
                                    value={
                                        subUsers
                                        ? subUsers.find(
                                        (subUser) =>
                                            subUser?.id.toString() ===
                                            formData?.userId?.toString()
                                        )
                                        : {}
                                    }
                                    onChange={(value) => {
                                    setFormData(formData => ({ ...formData, userId: value?.id || 0 }))
                                }}/>
                            </div>
                        }
                    </Form>
                </div>
            </ModalBody>

            <ModalFooter style={{ boxShadow: "2px 2px 2px 1px rgb(2 0 0 / 94%)" }}>
                <Button
                    disabled={loadingCreateEdit}
                    onClick={handleClose}
                    className="btn-cancel"
                >
                    {messages['cancel']}
                </Button>

                <Button
                    disabled={loadingCreateEdit || (formData?.assignedToUser && !formData?.userId)}
                    onClick={(e) => {
                        if (e) e.preventDefault();
                        editCreateAccountApiKey.current.submit();
                    }}
                    className="btn-blue"
                >
                    {apiKey ? messages['save'] : messages['create']}
                </Button>
            </ModalFooter>
        </Modal>
    );
}

export default injectIntl(CreateEditAccountApiKey);